import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button, Container } from '@/components/ui';
import { ApiError, NetworkError, ResponseShapeError } from '@/lib/api/errors';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-phase errors so one broken subtree cannot blank the whole app.
 *
 * This has to be a class component: `componentDidCatch` and
 * `getDerivedStateFromError` have no hook equivalent.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The component stack is what makes a production stack trace actionable;
    // the minified JS stack alone rarely is.
    console.error('Unhandled render error', error, info.componentStack);
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  private readonly reload = (): void => {
    window.location.reload();
  };

  /**
   * A message safe and useful to show a user.
   *
   * Our own error types already carry human-readable text; anything else could
   * be an internal message or leak implementation detail, so it is replaced.
   */
  private describe(error: Error): string {
    if (error instanceof ApiError || error instanceof NetworkError) return error.message;
    if (error instanceof ResponseShapeError) {
      return 'The server sent data this version of the app does not understand. Reloading usually fixes it.';
    }
    return 'Something went wrong while rendering this page.';
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const requestId = error instanceof ApiError ? error.requestId : null;

    return (
      <Container
        size="sm"
        className="flex min-h-dvh flex-col items-center justify-center py-20 text-center"
      >
        <p className="text-danger-500 text-sm font-semibold tracking-wider uppercase">Error</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight">This page hit a snag</h1>
        <p className="mt-4 text-[var(--text-secondary)]">{this.describe(error)}</p>

        {requestId ? (
          <p className="mt-4 font-mono text-xs text-[var(--text-muted)]">Request ID: {requestId}</p>
        ) : null}

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Button onClick={this.reset}>Try again</Button>
          <Button variant="secondary" onClick={this.reload}>
            Reload the app
          </Button>
        </div>
      </Container>
    );
  }
}
